import React from 'react';
import {
  Box,
  Card,
  CardContent,
  Switch,
  Typography,
  Chip,
  Button,
  FormControlLabel,
} from '@mui/material';
import { Settings as SettingsIcon } from '@mui/icons-material';
import { BossBabyToolDto, BossBabyCompanySettingDto } from '@/dto/BossBabyDto';

interface CompanyToolToggleProps {
  tool: BossBabyToolDto & { companySetting?: BossBabyCompanySettingDto };
  onToggle: (toolId: number, isEnabled: boolean) => void;
  onCustomConfig?: (toolId: number) => void;
}

export const CompanyToolToggle: React.FC<CompanyToolToggleProps> = ({
  tool,
  onToggle,
  onCustomConfig,
}) => {
  const getToolStatus = () => {
    if (tool.companySetting) {
      return tool.companySetting.isEnabled ? 'Company Enabled' : 'Company Disabled';
    }
    return tool.isActive ? 'Global Active' : 'Global Inactive';
  };

  const getStatusColor = () => {
    if (tool.companySetting) {
      return tool.companySetting.isEnabled ? 'success' : 'error';
    }
    return tool.isActive ? 'info' : 'default';
  };

  const isEnabled = tool.companySetting?.isEnabled ?? tool.isActive;

  return (
    <Card variant="outlined" sx={{ mb: 2 }}>
      <CardContent>
        <Box sx={{ display: 'flex', justifyContent: 'between', alignItems: 'flex-start', mb: 2 }}>
          <Box sx={{ flexGrow: 1 }}>
            <Typography variant="h6" component="div" sx={{ mb: 1 }}>
              {tool.toolName}
            </Typography>
            
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              {tool.description || 'No description provided'}
            </Typography>

            <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mb: 2 }}>
              <Chip 
                label={getToolStatus()} 
                size="small" 
                color={getStatusColor() as any}
              />
              <Chip 
                label={tool.priorityLevel} 
                size="small" 
                color={tool.priorityLevel === 'HIGH' ? 'error' : tool.priorityLevel === 'MEDIUM' ? 'warning' : 'info'}
              />
              <Chip 
                label={`${tool.timeoutSeconds}s timeout`} 
                size="small" 
                variant="outlined"
              />
            </Box>

            <Typography variant="caption" color="text.secondary" display="block">
              {tool.companySetting ? 'Company Setting: Custom' : 'Company Setting: Using Global'}
            </Typography>
          </Box>

          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 1 }}>
            <FormControlLabel
              control={
                <Switch
                  checked={isEnabled}
                  onChange={(e) => onToggle(tool.toolId, e.target.checked)}
                  color="primary"
                />
              }
              label={isEnabled ? 'Enabled' : 'Disabled'}
              labelPlacement="start"
              sx={{ m: 0 }}
            />

            {onCustomConfig && (
              <Button
                size="small"
                variant="outlined"
                startIcon={<SettingsIcon />}
                onClick={() => onCustomConfig(tool.toolId)}
              >
                Custom Config
              </Button>
            )}
          </Box>
        </Box>
      </CardContent>
    </Card>
  );
};